import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Mail, Users, Send, Repeat, AlertCircle, ArrowRight, Flame, CheckCircle2, Eye } from "lucide-react";
import { api } from "../../lib/api";
import { LeaOrb } from "../../components/Lea";

const STAT_ICONS = { new_emails: Mail, prospects: Users, to_validate: Send, followups: Repeat, urgent: AlertCircle };
const STAT_COLORS = {
  new_emails: "text-electric bg-electric/10",
  prospects: "text-blue-600 bg-blue-500/10",
  to_validate: "text-primary bg-primary/10 dark:text-blue-400",
  followups: "text-warning bg-warning/10",
  urgent: "text-destructive bg-destructive/10",
};

const KIND_ICON = { urgent: <AlertCircle size={15} className="text-destructive" />, reply: <Flame size={15} className="text-orange-500" />, followup: <Repeat size={15} className="text-warning" />, task: <CheckCircle2 size={15} className="text-success" /> };

export default function Dashboard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/dashboard").then((r) => setData(r.data)).catch(() => {}); }, [i18n.language]);

  if (!data) {
    return (
      <div data-testid="dashboard-loading" className="space-y-4">
        <div className="skeleton-shimmer h-9 w-72 rounded-lg" />
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {[...Array(5)].map((_, i) => <div key={i} className="skeleton-shimmer h-28 rounded-2xl" />)}
        </div>
        <div className="skeleton-shimmer h-64 rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter" data-testid="dashboard-greeting">
          {t("dashboard.hello", { name: data.first_name })}
        </h1>
        <p className="text-muted-foreground text-sm mt-1">{t("dashboard.subtitle")}</p>
      </motion.div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="dashboard-stats">
        {Object.keys(STAT_ICONS).map((key, i) => {
          const Icon = STAT_ICONS[key];
          return (
          <motion.div key={key} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.08 * i, duration: 0.5 }}
            className="rounded-2xl border border-border bg-card p-5 card-hover" data-testid={`stat-${key}`}>
            <div className={`h-9 w-9 rounded-xl flex items-center justify-center mb-4 ${STAT_COLORS[key]}`}>
              <Icon size={17} />
            </div>
            <p className="font-display font-black text-3xl tracking-tighter">{data.stats[key]}</p>
            <p className="text-xs text-muted-foreground mt-1">{t(`dashboard.stats.${key}`)}</p>
          </motion.div>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.5 }}
          className="lg:col-span-2 rounded-2xl border border-border bg-card p-6 sm:p-8" data-testid="priorities-section">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display font-bold text-xl tracking-tight">{t("dashboard.prioritiesTitle")}</h2>
            <button onClick={() => navigate("/app/priorites")} data-testid="see-all-priorities"
              className="text-xs font-semibold text-electric hover:underline flex items-center gap-1">
              {t("dashboard.seeAll")} <ArrowRight size={12} />
            </button>
          </div>
          {data.priorities.length === 0 ? (
            <div className="py-12 text-center">
              <CheckCircle2 size={32} className="mx-auto text-success mb-4" />
              <p className="text-sm text-muted-foreground">{t("dashboard.prioritiesEmpty")}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {data.priorities.map((p, i) => (
                <motion.div key={i} initial={{ opacity: 0, x: -14 }} animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.35 + i * 0.07 }}
                  className="flex items-center gap-4 rounded-xl border border-border/70 bg-background px-4 py-3.5"
                  data-testid={`priority-item-${i}`}>
                  {KIND_ICON[p.kind]}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold truncate">{p.title}</p>
                    <p className="text-xs text-muted-foreground truncate">{p.subtitle}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    {p.email_id && (
                      <button onClick={() => navigate(`/app/inbox/${p.email_id}`)} data-testid={`priority-reply-${i}`}
                        className="rounded-full bg-electric text-white text-[11px] font-bold px-3.5 py-1.5 hover:bg-blue-700 transition-colors">
                        {t("common.reply")}
                      </button>
                    )}
                    {p.followup_id && (
                      <button onClick={() => navigate("/app/relances")} data-testid={`priority-followup-${i}`}
                        className="rounded-full bg-warning text-white text-[11px] font-bold px-3.5 py-1.5 hover:opacity-90 transition-opacity">
                        {t("dashboard.followupAction")}
                      </button>
                    )}
                    <button
                      onClick={() => p.email_id ? navigate(`/app/inbox/${p.email_id}`) : p.task_id ? navigate("/app/taches") : navigate("/app/relances")}
                      data-testid={`priority-view-${i}`}
                      className="rounded-full border border-border text-[11px] font-semibold px-3.5 py-1.5 hover:border-electric/50 hover:text-electric transition-colors flex items-center gap-1">
                      <Eye size={11} /> {t("common.view")}
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.5 }}
          className="rounded-2xl border border-electric/25 bg-gradient-to-b from-electric/5 to-card p-6 sm:p-8 relative overflow-hidden"
          data-testid="lea-card">
          <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-electric/15 blur-3xl" />
          <div className="flex items-center gap-3 mb-6 relative">
            <LeaOrb size={44} />
            <div>
              <p className="font-display font-bold">{t("common.assistant")}</p>
              <p className="text-[11px] text-muted-foreground">{t("dashboard.leaRole")}</p>
            </div>
            <span className="ml-auto flex items-center gap-1.5 text-[10px] font-semibold text-success">
              <span className="h-1.5 w-1.5 rounded-full bg-success animate-pulse-soft" /> {t("dashboard.leaStatus")}
            </span>
          </div>
          <p className="text-sm leading-relaxed text-foreground/90 relative" data-testid="lea-message">{data.lea_message}</p>
          <button onClick={() => navigate("/app/priorites")} data-testid="lea-process-priorities"
            className="mt-6 w-full rounded-full bg-electric text-white py-3 text-xs font-bold hover:bg-blue-700 transition-colors relative">
            {t("dashboard.leaCta")}
          </button>
        </motion.div>
      </div>
    </div>
  );
}
